import {
  Stack, Text, Card, Group, Badge, Button, Center, Loader, Box, ScrollArea, Table, Alert,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useMigrateExcel, type MigrationResponse } from '../api/admin';
import { StatusBadge } from '../components/StatusBadge';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/ErrorState';
import { t } from '../lib/strings';
import { formatDateShort } from '../lib/dates';

export function AdminMigratePage() {
  const mutation = useMigrateExcel();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [result, setResult] = useState<MigrationResponse | null>(null);

  function onDrop(files: File[]) {
    const file = files[0];
    if (!file) return;
    setPendingFile(file);
    setResult(null);
    mutation.mutate({ file, commit: false }, { onSuccess: (r) => setResult(r) });
  }

  function commit() {
    if (!pendingFile) return;
    mutation.mutate(
      { file: pendingFile, commit: true },
      { onSuccess: (r) => { setResult(r); setPendingFile(null); } },
    );
  }

  const plan = result?.plan;
  const statusCounts = plan ? countStatuses(plan.cases) : null;

  return (
    <Stack gap="md">
      <PageHeader title={t.nav.admin} />

      <Alert color="yellow" icon={<IconAlertTriangle />}>
        Kertaluonteinen migraatio vanhasta <code>TAKSIKORTTI TUPLAT EXCEL.xlsx</code> -tiedostosta.
        Lataa tiedosto, tarkista koeajon tulos ja vahvista vasta kun se näyttää oikealta.
      </Alert>

      <Dropzone
        onDrop={onDrop}
        onReject={() => notifications.show({ color: 'red', message: t.errors.fileRejected })}
        accept={{ 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }}
        maxFiles={1}
        maxSize={50 * 1024 * 1024}
        loading={mutation.isPending}
      >
        <Group justify="center" gap="md" mih={120} style={{ pointerEvents: 'none' }}>
          <IconUpload size={32} />
          <Text>Vedä .xlsx-tiedosto tähän — koeajo tehdään automaattisesti</Text>
        </Group>
      </Dropzone>

      {mutation.isPending && (
        <Center p="md"><Group gap="xs"><Loader size="sm" /><Text>Luetaan tiedostoa…</Text></Group></Center>
      )}

      {mutation.isError && (
        <ErrorState
          error={mutation.error}
          {...(pendingFile ? { onRetry: () => mutation.mutate({ file: pendingFile, commit: false }, { onSuccess: (r) => setResult(r) }) } : {})}
        />
      )}

      {plan && (
        <Card withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={500}>{result?.dryRun ? 'Koeajon tulos' : 'Migraatio suoritettu'}</Text>
              {result?.dryRun && pendingFile && (
                <Button color="red" onClick={commit} loading={mutation.isPending}>
                  Vahvista ja tallenna ({plan.cases.length} tapausta)
                </Button>
              )}
            </Group>

            <Group gap="md">
              <Stat label="Tapauksia" value={plan.cases.length} />
              <Stat label="Ohitettuja" value={plan.demoted.length} />
              <Stat label="Matkoja tuotu" value={plan.trips.imported} />
              <Stat label="Matkoja ohitettu" value={plan.trips.skipped} />
              <Stat label="Matkavirheitä" value={plan.trips.failures.length} />
            </Group>

            {statusCounts && (
              <Group gap="xs">
                {Object.entries(statusCounts).map(([s, n]) => (
                  <Badge key={s} variant="light" size="lg">
                    {(t.status as Record<string, string>)[s] ?? s}: {n}
                  </Badge>
                ))}
              </Group>
            )}

            {plan.warnings.length > 0 && (
              <Alert color="orange" icon={<IconAlertTriangle />}>
                <Stack gap={4}>
                  {plan.warnings.map((w, i) => (<Text key={i} size="sm">{w}</Text>))}
                </Stack>
              </Alert>
            )}

            {plan.cases.length > 0 && (
              <Box>
                <Text size="sm" fw={500} mb="xs">Ensimmäiset 50 tapausta</Text>
                <ScrollArea h={300}>
                  <Table fz="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Rivi</Table.Th>
                        <Table.Th>Kortti</Table.Th>
                        <Table.Th>Tila</Table.Th>
                        <Table.Th>Ensim. yhteydenotto</Table.Th>
                        <Table.Th>Viim. yhteydenotto</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {plan.cases.slice(0, 50).map((c) => (
                        <Table.Tr key={c.caseId}>
                          <Table.Td>{c.sourceRow}</Table.Td>
                          <Table.Td>{c.korttinumero}</Table.Td>
                          <Table.Td>
                            <StatusBadge status={c.status} closedReason={c.closedReason} />
                          </Table.Td>
                          <Table.Td>{formatDateShort(c.firstContactAt)}</Table.Td>
                          <Table.Td>{formatDateShort(c.lastContactAt)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Box>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Text size="lg" fw={600}>{value}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </Box>
  );
}

function countStatuses(cases: { status: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of cases) out[c.status] = (out[c.status] ?? 0) + 1;
  return out;
}
